import { createContext, PropsWithChildren, use, useMemo, type JSX, ReactNode } from 'react'

import { Table } from '@radix-ui/themes'

import { TableAction, TableColumn, TableGlobalAction, TFilter } from '..'
import { TTableStoreApi } from '../store/table-store'
import { TTableStore } from '../types/table-store'

export type TTableContext<T> = {
    id: keyof T
    store: TTableStoreApi<T>
    defaultPageSize: number
    /** true when rows come from `getData` (already one page), false for static `data` */
    serverSide: boolean
    hidePagination: boolean
    hideHeaderRow: boolean
    draggable: boolean
    expandable?: {
        expandedRowRender: (value: T) => (JSX.Element | undefined)
    }
    /** all columns, hidden ones included; use `useVisibleColumns` for the rendered ones */
    columns: TableColumn<T>[]
    globalActions?: TableGlobalAction<T>[]
    saveFilters: boolean | string
    toggleableColumns: Set<string>
    selectable: boolean
    classNameGenerator?: (row: T) => string
    customRowRender?: (row: T) => ReactNode
    tableActionsBreakpoint: number
    generateRowProps?: (row: T) => Table.RowProps
    actions: TableAction<T>[]
    onRowSelected?: (selectedRows: T[]) => void
    onHiddenColumnsChanged?: (hiddenColumns: string[]) => void
    onReorder?: (rows: T[]) => void | Promise<void>
    calculateSummary?: (filters: TFilter[]) => Promise<any>
}

// the context value is generic per table; `unknown` is narrowed back in useTableContext
const TableContext = createContext<TTableContext<unknown> | undefined>(undefined)

type TProps<T> = PropsWithChildren & {
    data: TTableContext<T>
}

export const TableContextProvider = <T,>({ children, data }: TProps<T>) => (
    <TableContext value={data as TTableContext<unknown>}>{children}</TableContext>
)

export const useTableStore = <T, R extends keyof TTableStore<T>>(
    selector: R
): TTableStore<T>[R] => {
    const { store } = useTableContext<T>()
    return store.useStoreWithSelector(selector)
}

export const useTableStoreFn = <T, R>(selector: (store: TTableStore<T>) => R): R => {
    const { store } = useTableContext<T>()
    return store.useStoreWithSelectorFunction(selector)
}

export const useTableContext = <T,>() => {
    const context = use(TableContext)
    if (!context)
        throw new Error('useTableContext must be used inside TableProvider')

    return context as unknown as TTableContext<T>
}

/** The columns currently rendered: `hiddenColumns` hold indexes into the *full* column list. */
export const filterVisibleColumns = <T,>(columns: TableColumn<T>[], hiddenColumns: string[]) =>
    columns.filter((column, index) => !(column.toggleable && hiddenColumns.includes(index.toString())))

export const useVisibleColumns = <T,>() => {
    const { columns } = useTableContext<T>()
    const hiddenColumns = useTableStore<T, 'hiddenColumns'>('hiddenColumns')

    return useMemo(() => filterVisibleColumns(columns, hiddenColumns), [columns, hiddenColumns])
}
